import { useState } from "react";
import { observer } from "mobx-react-lite";
import {
    deactivateOrganisation,
    DeactivateOrganisationData,
    GetOrganisationData,
    grantDemoAccess,
    revokeDemoAccess,
} from "@/api";
import { appState } from "@/app/appState";
import { organisation_tree_node } from "@/app/typing/organisations";
import { Request } from "@/components/CRUD";
import { ActionHandler } from "@/components/UI/tools";
import { canWrite } from "./tree";
import { Row, Section } from "./ui";

/** Demo access and deactivation. The tab is only offered to super admins. */
export const DangerSection = observer(({
    node,
    onRefresh,
    onDeactivated,
}: {
    node: organisation_tree_node;
    onRefresh: () => void;
    onDeactivated: () => void;
}) => {
    const writable = canWrite(node);
    const isSuperAdmin = !!appState.user?.is_super_admin;
    const isActive = node.id === appState.user?.active_organisation?.id;
    const [loading, setLoading] = useState(false);

    const deactivate = Request<DeactivateOrganisationData, any>(
        deactivateOrganisation,
        () => ({ body: { id: node.id } }),
        setLoading,
        () => onDeactivated(),
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("settings.org.deactivate"),
            }),
        () => writable,
    );

    const demoError = (error: any) =>
        appState.setModal({
            title: appState.t("common.error"),
            body: error.detail?.toString() ?? appState.t("settings.org.demoAccess"),
        });

    const grantDemo = Request<GetOrganisationData, any>(
        grantDemoAccess,
        () => ({ body: { id: node.id } }),
        setLoading,
        () => onRefresh(),
        demoError,
        () => isSuperAdmin,
    );

    const revokeDemo = Request<GetOrganisationData, any>(
        revokeDemoAccess,
        () => ({ body: { id: node.id } }),
        setLoading,
        () => onRefresh(),
        demoError,
        () => isSuperAdmin,
    );

    if (!isSuperAdmin) return null;

    return (
        <Section title={appState.t("settings.org.danger")}>
            <Row
                trailing={
                    <ActionHandler
                        title={node.demo_access ? appState.t("settings.org.revokeDemo") : appState.t("settings.org.grantDemo")}
                        funq={() => (node.demo_access ? revokeDemo() : grantDemo())}
                        disabled={loading}
                        glow={node.demo_access}
                    >
                        {node.demo_access ? appState.t("settings.org.revokeDemo") : appState.t("settings.org.grantDemo")}
                    </ActionHandler>
                }
            >
                <p className="l">{appState.t("settings.org.demoAccess")}</p>
                <p className="detail-text">{appState.t("settings.org.demoAccessHint")}</p>
            </Row>
            {writable && (
                <Row
                    trailing={
                        <ActionHandler
                            title={appState.t("settings.org.deactivate")}
                            funq={() =>
                                appState.setModal({
                                    title: appState.t("settings.org.deactivateConfirm", { name: node.name }),
                                    body: appState.t("settings.org.deactivateBody"),
                                    action: () => deactivate(),
                                })
                            }
                            disabled={loading || isActive}
                            className="btn-danger"
                        >
                            {appState.t("settings.org.deactivate")}
                        </ActionHandler>
                    }
                >
                    <p className="l">{appState.t("settings.org.deactivate")}</p>
                    <p className="detail-text">
                        {isActive
                            ? appState.t("settings.org.cannotDeactivateActive")
                            : appState.t("settings.org.deactivateHint")}
                    </p>
                </Row>
            )}
        </Section>
    );
});
